import React, { useState } from 'react';
import { PimProductT } from '@lambda/apis/src/dynamoDb/types';
import { invert, uniq } from 'lodash-es';
import { ReebeloLanguage } from '@lambda/reebelo';
import Variant from '../Variant';
import i18n from '@/settings/i18n';
import {
  ConnectivitieSkus,
  OfferTree,
  SelectionT,
} from '@/lib/collections/catalogue/helpers';
import * as ColorVariant from '../ColorVariant';
import ColorSwatch from './ColorSwatch';

const t = i18n.init();

interface SmartWatchesProps {
  product: PimProductT;
  offerTree: OfferTree;
  selection: SelectionT;
  language: ReebeloLanguage;
  isUnfitProduct: boolean;
  setSuggestedVariant: React.Dispatch<React.SetStateAction<string>>;
  suggestedVariant: string;
}

const getWatchColors = (
  offerTree: OfferTree,
  product: PimProductT,
  selection: SelectionT,
  colorType: string,
) => {
  if (colorType === 'Band') {
    return uniq(
      offerTree.orderedPath.map((offer) => offer.split(',')[6]),
    ).filter((color) =>
      Object.values(product.watchBandColors).find((c) => c === color),
    );
  }

  if (colorType === 'Case') {
    return uniq(
      offerTree.orderedPath
        .filter(
          (offer) =>
            offer.split(',')[1] === selection.watchFaceSize &&
            offer.split(',')[0] === selection.watchCaseMaterial,
        )
        .map((offer) => offer.split(',')[2]),
    ).filter((color) =>
      Object.values(product.watchCaseColors).find((c) => c === color),
    );
  }

  return [];
};

export default function SmartWatchesVariants({
  product,
  offerTree,
  selection,
  language,
  isUnfitProduct,
  suggestedVariant,
  setSuggestedVariant,
}: SmartWatchesProps) {
  const [hoverCaseColor, setHoverCaseColor] = useState('');
  const [hoverBandColor, setHoverBandColor] = useState('');
  const bandColors =
    product.locale?.[language]?.watchBandColors ?? product.watchBandColors;
  const caseColors =
    product.locale?.[language]?.watchCaseColors ?? product.watchCaseColors;
  const bandColorsToCodeMapping = invert(bandColors);
  const caseColorsToCodeMapping = invert(caseColors);

  // we want only watch case colors match watch face size and watch case material from offers
  const watchCaseColors = getWatchColors(offerTree, product, selection, 'Case');
  // we want only band colors from offers
  const watchBandColors = getWatchColors(offerTree, product, selection, 'Band');

  const filteredVariants = (variants: string[], index: number) =>
    variants?.length
      ? variants.filter((sp) =>
          offerTree.orderedPath.find((path) => {
            const p = path.split(',');

            return p[index] === sp;
          }),
        )
      : [];

  const watchCaseMaterials = filteredVariants(product.watchCaseMaterials, 0);
  const watchFaceSizes = filteredVariants(product.watchFaceSizes, 1).sort(
    (a, b) => {
      const numA = parseInt(a.replace('mm', ''), 10);
      const numB = parseInt(b.replace('mm', ''), 10);

      return numA - numB;
    },
  );
  const watchBandMaterials = filteredVariants(product.watchBandMaterials, 4);
  const watchBandTypes = filteredVariants(product.watchBandTypes, 5);
  const connectivities = product.connectivities.filter((c) =>
    offerTree.orderedPath.find((path) => {
      const p = path.split(',');

      return p[3] === ConnectivitieSkus[language][c];
    }),
  );

  return (
    <>
      <Variant.Group
        title={t`Case`}
        psku={product.psku}
        className="grid grid-cols-2 xxs:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3"
        id="e2e-product-watchCaseMaterial"
        suggestedVariant={suggestedVariant}
      >
        {watchCaseMaterials.map((e) => (
          <Variant.Anchor
            disabled={isUnfitProduct}
            variant="case"
            setSuggestedVariant={setSuggestedVariant}
            key={e}
            className="relative px-5 sm:px-8"
            query={offerTree.getFrom(
              {
                watchCaseMaterial: e,
              },
              selection,
              0,
            )}
            active={selection.watchCaseMaterial === e}
          >
            {t(e)}
          </Variant.Anchor>
        ))}
      </Variant.Group>
      <Variant.Group
        title={t`Case Size`}
        psku={product.psku}
        className={`grid grid-cols-2`}
        id="e2e-product-watchFaceSize"
        suggestedVariant={suggestedVariant}
      >
        {watchFaceSizes.map((e) => (
          <Variant.Anchor
            disabled={isUnfitProduct}
            key={e}
            setSuggestedVariant={setSuggestedVariant}
            variant="case size"
            className="relative px-5 sm:px-8"
            query={offerTree.getFrom(
              {
                watchCaseMaterial: selection.watchCaseMaterial,
                watchFaceSize: e,
              },
              selection,
              1,
            )}
            active={selection.watchFaceSize === e}
          >
            {t(e)}
          </Variant.Anchor>
        ))}
      </Variant.Group>
      <ColorVariant.Group
        title={t`Case Color`}
        psku={product.psku}
        selectedColor={
          hoverCaseColor !== '' ? hoverCaseColor : selection.watchCaseColor
        }
        id="e2e-product-watchCaseColor"
        suggestedVariant={suggestedVariant}
      >
        {Object.values(watchCaseColors)
          .sort()
          .map((e) => {
            // if color is not defined fall back to white color
            // if loading placeholder color is grey

            const currentColorCode = product.watchCaseColorsInHex
              ? product.watchCaseColorsInHex[caseColorsToCodeMapping[e]] ||
                '#ffffff'
              : '#cccccc';
            const query = offerTree.getFrom(
              {
                watchCaseMaterial: selection.watchCaseMaterial,
                watchFaceSize: selection.watchFaceSize,
                watchCaseColor: e,
              },
              selection,
              2,
            );

            return (
              <ColorVariant.Anchor
                disabled={isUnfitProduct}
                key={e}
                query={query}
                currentColor={e}
                active={selection.watchCaseColor === e}
                onHover={(c) => setHoverCaseColor(c)}
                onHoverOut={() => setHoverCaseColor('')}
                setSuggestedVariant={setSuggestedVariant}
                variant="case color"
              >
                <ColorSwatch
                  color={currentColorCode}
                  active={selection.watchCaseColor === e}
                  outOfStock={query === null}
                />
              </ColorVariant.Anchor>
            );
          })}
      </ColorVariant.Group>
      <Variant.Group
        title={t`Connectivity`}
        psku={product.psku}
        className="grid grid-cols-2"
        id="e2e-product-connectivity"
        suggestedVariant={suggestedVariant}
      >
        {connectivities
          .map((c) => ConnectivitieSkus[language][c])
          .map((e) => (
            <Variant.Anchor
              disabled={isUnfitProduct}
              key={e}
              setSuggestedVariant={setSuggestedVariant}
              variant="connectivity"
              className="relative px-5 sm:px-8"
              query={offerTree.getFrom(
                {
                  watchCaseMaterial: selection.watchCaseMaterial,
                  watchFaceSize: selection.watchFaceSize,
                  watchCaseColor: selection.watchCaseColor,
                  connectivity: e,
                },
                selection,
                3,
              )}
              active={selection.connectivity === e}
            >
              {t(e)}
            </Variant.Anchor>
          ))}
      </Variant.Group>
      <Variant.Group
        title={t`Band Materials`}
        psku={product.psku}
        className="grid grid-cols-2 xxs:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3"
        id="e2e-product-watchBandMaterial"
        suggestedVariant={suggestedVariant}
      >
        {watchBandMaterials.map((e) => (
          <Variant.Anchor
            disabled={isUnfitProduct}
            key={e}
            setSuggestedVariant={setSuggestedVariant}
            variant="band materials"
            className="relative px-5 sm:px-8"
            query={offerTree.getFrom(
              {
                watchCaseMaterial: selection.watchCaseMaterial,
                watchFaceSize: selection.watchFaceSize,
                watchCaseColor: selection.watchCaseColor,
                connectivity: selection.connectivity,
                watchBandMaterial: e,
              },
              selection,
              4,
            )}
            active={selection.watchBandMaterial === e}
          >
            {t(e)}
          </Variant.Anchor>
        ))}
      </Variant.Group>
      <Variant.Group
        title={t`Band Type`}
        psku={product.psku}
        className="grid grid-cols-2 xxs:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3"
        id="e2e-product-watchBandType"
        suggestedVariant={suggestedVariant}
      >
        {watchBandTypes.map((e) => (
          <Variant.Anchor
            disabled={isUnfitProduct}
            key={e}
            setSuggestedVariant={setSuggestedVariant}
            variant="band type"
            className="relative px-5 sm:px-8"
            query={offerTree.getFrom(
              {
                watchCaseMaterial: selection.watchCaseMaterial,
                watchFaceSize: selection.watchFaceSize,
                watchCaseColor: selection.watchCaseColor,
                connectivity: selection.connectivity,
                watchBandMaterial: selection.watchBandMaterial,
                watchBandType: e,
              },
              selection,
              5,
            )}
            active={selection.watchBandType === e}
          >
            {t(e)}
          </Variant.Anchor>
        ))}
      </Variant.Group>
      <ColorVariant.Group
        title={t`Band Color`}
        psku={product.psku}
        selectedColor={
          hoverBandColor !== '' ? hoverBandColor : selection.watchBandColor
        }
        id="e2e-product-watchBandColor"
        suggestedVariant={suggestedVariant}
      >
        {Object.values(watchBandColors)
          .sort()
          .map((e) => {
            // if color is not defined fall back to white color
            // if loading placeholder color is grey

            const currentColorCode = product.watchBandColorsInHex
              ? product.watchBandColorsInHex[bandColorsToCodeMapping[e]] ||
                '#ffffff'
              : '#cccccc';
            const query = offerTree.getFrom(
              {
                watchCaseMaterial: selection.watchCaseMaterial,
                watchFaceSize: selection.watchFaceSize,
                connectivity: selection.connectivity,
                watchBandMaterial: selection.watchBandMaterial,
                watchBandType: selection.watchBandType,
                watchCaseColor: selection.watchCaseColor,
                watchBandColor: e,
              },
              selection,
              6,
            );

            return (
              <ColorVariant.Anchor
                disabled={isUnfitProduct}
                key={e}
                query={query}
                currentColor={e}
                active={selection.watchBandColor === e}
                onHover={(c) => setHoverBandColor(c)}
                onHoverOut={() => setHoverBandColor('')}
                setSuggestedVariant={setSuggestedVariant}
                variant="band color"
              >
                <ColorSwatch
                  color={currentColorCode}
                  active={selection.watchBandColor === e}
                  outOfStock={query == null}
                />
              </ColorVariant.Anchor>
            );
          })}
      </ColorVariant.Group>
    </>
  );
}
